import { useLocalStorage, watchOnce } from '@vueuse/core'
import { defineStore } from 'pinia'
import { findLast, takeRightWhile } from 'lodash-es'
import dayjs from 'dayjs'
import { type ServiceRecordForQuery, type ZoneInfo, getAllServiceRecords, getServiceInfo, getZoneInfo, heartbeat, heartbeatAuthed, queryWaitingInfo, requestWelcome } from '~/api'
import { useServiceRecord } from '~/composables/service'
import { createLocalCustomMsg, queryHistory } from '~/utils/nim'
import { DAY, DEFAULT_REMOTE_HISTORY_QUERY_DAYS, ROBOT_ACCOUNT, ROBOT_NICK_NAME, SINGLE_PAGE_MSG_MAX, VISITOR_OFFLINE_MSG_ENABLED } from '~/constants'
import type { ClientMessageType } from '~/utils/tools'
import { isServiceClosed, isServiceFinished, queryIdb, updateIdb } from '~/utils/tools'
import * as bus from '~/utils/bus'
import { isAICannotResolve } from '~/utils/ai'

export const useChat = defineStore('chat', () => {
  const imToken = ref('')
  const imAccount = ref('')
  const isVisitor = ref(false)
  const connected = ref(false)
  const connectDone = ref(false)
  const showUrgentNotice = ref(false)
  watchOnce(connected, (v) => {
    if (v)
      connectDone.value = true
  })
  const reconnecting = ref(false)

  const messages = ref<Message[]>([])
  const currentService = useServiceRecord()
  const waiting = ref(false)

  const settings = ref<ZoneInfo>()
  const canChat = computed(() => settings.value?.allowVisitor === 'YES' || !isVisitor.value)
  const historyDone = ref(false)
  const historyLoading = ref(false)
  const historyExhaust = ref(false)
  const historyIndex = ref(Date.now())
  const serviceInfoInitted = ref(false)

  let initSettingsRetryRemains = 3
  async function initSettings() {
    if (!useZone().value.zoneCode)
      return
    const [err, res] = await getZoneInfo()
    if (err) {
      if (initSettingsRetryRemains <= 0)
        return
      initSettingsRetryRemains--
      setTimeout(() => {
        initSettings()
      }, 1000)
    }
    else {
      settings.value = res.data
    }
  }

  function clearSettings() {
    settings.value = undefined
  }

  function handleMessage(msg: Message) {
    if (msg.custom && !msg.customDecoded)
      msg.customDecoded = JSON.parse(msg.custom)
    if (msg.type === 'custom' && msg.content && !msg.contentDecoded)
      msg.contentDecoded = JSON.parse(msg.content)

    if (msg.contentDecoded) {
      const { type } = msg.contentDecoded

      if (type === 'welcome' || type === 'questionReply' || type === 'aiDefaultReply')
        msg.fromNick = settings.value?.robotNickname || ROBOT_NICK_NAME
    }

    if (msg.from === ROBOT_ACCOUNT)
      msg.fromNick = settings.value?.robotNickname || ROBOT_NICK_NAME

    return msg
  }

  function insertMsg(list: Message[], msg: Message) {
    if (list.length === 0)
      return [msg]
    let lowerBound
    for (let i = list.length - 1; i >= 0; --i) {
      const item = list[i]
      if (item.time <= msg.time) {
        if (item.idServer && msg.idServer && item.idServer === msg.idServer)
          return list
        lowerBound = i
        break
      }
    }
    if (lowerBound)
      return [...list.slice(0, lowerBound + 1), msg, ...list.slice(lowerBound + 1)]
    else
      return [msg, ...list]
  }

  function deleteMessage(idServer: string) {
    messages.value = toRaw(messages.value).filter(x => x.idServer !== idServer)
    if (isVisitor.value)
      updateDb()
  }

  function updateDb() {
    updateIdb(toRaw(messages.value).filter(x => !x.isLocal))
  }

  function addMessage(msg: Message, type: ClientMessageType) {
    msg = handleMessage(msg)
    if (msg.customDecoded?.zoneCode && useZone().value.zoneCode !== msg.customDecoded.zoneCode)
      return
    if (type === 'IN_OFFLINE') {
      const SERVICE_MISMATCH_OR_EMPTY = !currentService.value?.serviceRecordId || isServiceFinished(currentService.value.status) || msg.customDecoded?.serviceRecordId !== currentService.value.serviceRecordId
      if (SERVICE_MISMATCH_OR_EMPTY && isVisitor.value && !useLocalStorage(`${VISITOR_OFFLINE_MSG_ENABLED}-${useZone().value.zoneCode!}`, false))
        return msg
    }
    if (type === 'IN_REALTIME') {
      // get service record from msg
      if (msg.customDecoded?.serviceRecordId) {
        if (!currentService.value?.serviceRecordId) {
          const { seatUserId, serviceRecordId, status } = msg.customDecoded
          if (serviceRecordId)
            currentService.value = { seatUserId, serviceRecordId, status }
        }
        else if (currentService.value.serviceRecordId === msg.customDecoded.serviceRecordId && msg.customDecoded.status) {
          currentService.value.status = msg.customDecoded.status
        }
      }
    }

    if (type === 'IN_OFFLINE')
      messages.value = insertMsg(toRaw(messages.value), msg)
    else
      messages.value.push(msg)

    if (type === 'IN_OFFLINE' || type === 'IN_REALTIME') {
      if (msg.contentDecoded?.type === 'aiDefaultReply')
        bus.emit(bus.keys.AI_TO_HUMAN)
      if (msg.contentDecoded?.type === 'questionReply' && msg.contentDecoded.content?.type === 'markdown' && isAICannotResolve(msg.contentDecoded.content?.answer)) {
        addMessage(
          createLocalCustomMsg('request-human'),
          'IN_FAKE',
        )
      }
    }

    if (isVisitor.value && !msg.isLocal && type !== 'IN_FAKE')
      updateDb()

    bus.emit(bus.keys.SEE_IF_NEED_SCROLL_TO_BOTTOM, msg.flow === 'out')
    return findLast(messages.value, x => x.idClient === msg.idClient)
  }

  interface SystemNoticeContentBase {
    type: 'assignSeat' | 'assignNightSeat' | 'inService' | 'closeService' | 'finishService'
    content: unknown
  }

  interface SystemNoticeContentAssignSeat extends SystemNoticeContentBase {
    type: 'assignSeat'
    content: {
      serviceRecordId: number
      seatUserId: string
      seatNickname: string
    }
  }

  interface SystemNoticeContentAssignNightSeat extends SystemNoticeContentBase {
    type: 'assignNightSeat'
    content: {
      serviceRecordId: number
      seatUserId: string
      seatNickname: string
    }
  }

  interface SystemNoticeContentInService extends SystemNoticeContentBase {
    type: 'inService'
    content: {
      serviceRecordId: number
      seatUserId: string
      seatNickname: string
    }
  }

  interface SystemNoticeContentCloseService extends SystemNoticeContentBase {
    type: 'closeService'
    content: {
      serviceRecordId: number
    }
  }

  interface SystemNoticeContentFinishService extends SystemNoticeContentBase {
    type: 'finishService'
    content: {
      serviceRecordId: number
    }
  }

  type SystemNoticeContent =
    | SystemNoticeContentAssignSeat
    | SystemNoticeContentAssignNightSeat
    | SystemNoticeContentInService
    | SystemNoticeContentCloseService
    | SystemNoticeContentFinishService

  async function addSystemMessage(msg: SystemNotice) {
    const parsed = msg.content ? (JSON.parse(msg.content) as SystemNoticeContent) : undefined

    if (!parsed)
      return

    if (!serviceInfoInitted.value)
      return

    // eslint-disable-next-line no-console
    console.log(`%c系统通知`, 'padding: 0 0.5em; background-color: rgb(118 132 254); color: white; border-radius: 2px;', parsed.type)

    if (parsed.type === 'assignSeat' || parsed.type === 'assignNightSeat') {
      currentService.value = {
        seatUserId: parsed.content.seatUserId,
        serviceRecordId: parsed.content.serviceRecordId,
        status: 'INIT',
      }

      const waitingContent = {
        seatName: parsed.content.seatNickname,
        night: parsed.type === 'assignNightSeat',
      }
      const addWaitingMsg = () => {
        addMessage(
          createLocalCustomMsg('waiting', waitingContent),
          'IN_FAKE',
        )
      }
      const waiting = findLast(messages.value.slice(-3), x => x.type === 'custom' && (x.contentDecoded?.type === 'waiting' || x.contentDecoded?.type === 'finishService'))
      if (waiting) {
        const waitingOrFinishContent = waiting.contentDecoded as CustomContentWaiting | CustomContentFinishService
        if (waitingOrFinishContent.type === 'finishService')
          return addWaitingMsg()

        if (waitingOrFinishContent.content.seatName) {
          if (waitingOrFinishContent.content.seatName !== parsed.content.seatNickname)
            addWaitingMsg()
        }
        else {
          waitingOrFinishContent.content = waitingContent
        }
      }
      else {
        addWaitingMsg()
      }
    }
    else if (parsed.type === 'inService') {
      currentService.value = {
        seatUserId: parsed.content.seatUserId,
        serviceRecordId: parsed.content.serviceRecordId,
        status: 'IN_SERVICE',
      }
    }
    else if (parsed.type === 'closeService') {
      if (currentService.value?.serviceRecordId && currentService.value.serviceRecordId === parsed.content.serviceRecordId) {
        if (currentService.value.status === 'FINISH')
          await processCurrentService(true)
        else
          currentService.value.status = 'TIMEOUT_CLOSE'
      }
    }
    else if (parsed.type === 'finishService') {
      if (currentService.value?.serviceRecordId && currentService.value.serviceRecordId === parsed.content.serviceRecordId) {
        currentService.value.status = 'FINISH'

        addMessage(
          createLocalCustomMsg('finishService', {}),
          'IN_FAKE',
        )
      }
    }
  }

  const records = ref<ServiceRecordForQuery[]>([])

  watchOnce(connected, async (v) => {
    if (v) {
      if (!isVisitor.value) {
        await loadServiceRecords().catch((err) => {
          console.error(err)
        })
      }
      await loadHistory().catch((err) => {
        console.error(err)
      })

      await processCurrentService().catch((err) => {
        console.error(err)
      })

      serviceInfoInitted.value = true

      setTimeout(() => {
        // pull the robot welcome if there isn't one
        if (settings.value?.receiveSubjectList.includes('QUESTION')
          && (!currentService.value?.serviceRecordId || isServiceFinished(currentService.value.status))
          && !messages.value.some(msg => msg.type === 'custom' && msg.contentDecoded?.type === 'welcome'))
          requestWelcome()
      })

      historyDone.value = true
      maintainConnection()
    }
  })

  async function loadServiceRecords() {
    records.value = await getAllServiceRecords(isVisitor.value ? useVisitorCode(useZone().value.zoneCode!).value : undefined)
  }

  function createGroups(records: ServiceRecordForQuery[], start: number, end: number) {
    return records.map((record) => {
      return {
        ...record,
        serviceTimeList: record.serviceTimeList.filter((item) => {
          return !(item.startTime > end || item.endTime < start)
        }).map((item) => {
          return { startTime: Math.max(start, item.startTime), endTime: Math.min(end, item.endTime) }
        }),
      }
    }).filter(record => record.serviceTimeList.length > 0)
  }

  function filterByZone(messages: Message[]) {
    const currZoneCode = useZone().value.zoneCode
    return messages.filter(msg => !msg.customDecoded?.zoneCode || !currZoneCode || msg.customDecoded.zoneCode === currZoneCode)
  }

  let hasEval = false
  let hasWelcome = false
  function filterOutWelcomeAndEval(messages: Message[]) {
    const result: Message[] = []
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]
      if (msg.type === 'custom' && msg.contentDecoded?.type === 'evaluateInvite') {
        if (!hasEval) {
          hasEval = true
          result.unshift(msg)
        }
      }
      else if (msg.type === 'custom' && msg.contentDecoded?.type === 'welcome') {
        if (!hasWelcome && dayjs().subtract(1, 'day').isBefore(msg.time)) {
          hasWelcome = true
          result.unshift(msg)
        }
      }
      else {
        result.unshift(msg)
      }
    }
    return result
  }

  // - for authed user: first-time query from IndexedDB by using IM client, later query from IM server
  // - for visitor: query from IndexedDB by using custom idb-keyval, keyed by zoneCode)
  async function loadHistory() {
    if (isVisitor.value) {
      const history: Message[] = await queryIdb()
      messages.value = [...filterOutWelcomeAndEval(history), ...toRaw(messages.value)]
      historyExhaust.value = true
    }
    else {
      const end = historyIndex.value
      const start = end - DEFAULT_REMOTE_HISTORY_QUERY_DAYS * DAY
      const lastMsg = messages.value.find(x => x.idServer)
      const groups = (() => {
        const result = createGroups(records.value, start, end)
        result.push({
          to: ROBOT_ACCOUNT,
          imAccount: imAccount.value,
          imToken: imToken.value,
          serviceTimeList: [{ startTime: start, endTime: end }],
        })
        return result
      })()

      const history = await queryHistory(groups, lastMsg?.idServer, lastMsg?.time)
      historyIndex.value -= DEFAULT_REMOTE_HISTORY_QUERY_DAYS * DAY
      if (history.length === 0) {
        if (groups.filter(x => x.to !== ROBOT_ACCOUNT).length === 0)
          historyExhaust.value = true
        else
          return loadHistory()
      }
      else {
        messages.value = [
          ...filterOutWelcomeAndEval(filterByZone(history.map(handleMessage))),
          ...toRaw(messages.value),
        ]
        if (messages.value.length < SINGLE_PAGE_MSG_MAX && !historyExhaust.value)
          return loadHistory()
      }
    }
  }

  async function loadMoreHistory() {
    if (historyExhaust.value || historyLoading.value)
      return
    historyLoading.value = true
    await loadHistory()
    historyLoading.value = false
  }

  async function maintainConnection() {
    if (connected.value) {
      if (isVisitor.value || !currentService.value?.serviceRecordId || isServiceFinished(currentService.value.status))
        await (isVisitor.value ? heartbeat() : heartbeatAuthed())
    }
    setTimeout(() => {
      maintainConnection()
    }, 60 * 1000)
  }

  async function processCurrentService(silent = false) {
    const [err, res] = await queryWaitingInfo()
    if (err)
      return

    const { beforeCount, ...others } = res.data
    if (others.serviceRecordId) {
      const [err, res] = await getServiceInfo(others.serviceRecordId)
      if (err)
        return
      currentService.value = {
        serviceRecordId: others.serviceRecordId,
        seatUserId: others.seatUserId,
        status: res.data.status,
      }
      if (res.data.status === 'IN_SERVICE' || res.data.status === 'INIT' || isServiceClosed(res.data.status)) {
        if (!silent || shouldShowEvenSilent()) {
          addMessage(
            createLocalCustomMsg('waiting', {
              seatName: res.data.seatNickname,
              night: res.data.seatMode === 'NIGHT',
            }),
            'IN_FAKE',
          )
        }
      }
      else if (res.data.status === 'FINISH') {
        if (silent) {
          addMessage(
            createLocalCustomMsg('finishService', {}),
            'IN_FAKE',
          )
        }
      }
    }
    else if (beforeCount != null) {
      if (!silent || shouldShowEvenSilent()) {
        addMessage(
          createLocalCustomMsg('waiting', { count: beforeCount, seatOnline: others.seatOnline === 'YES' }),
          'IN_FAKE',
        )
      }
      currentService.value = undefined
      waiting.value = true
    }
    else {
      currentService.value = undefined
    }
  }

  function shouldShowEvenSilent() {
    const toSearch = takeRightWhile(messages.value, msg => !(msg.type === 'custom' && msg.contentDecoded?.type === 'finishService'))
    const seat = findLast(toSearch, msg => msg.type === 'custom' && msg.contentDecoded?.type === 'waiting' && msg.contentDecoded?.content.seatName)
    const waitingNearBy = findLast(toSearch.slice(-5), msg => msg.type === 'custom' && msg.contentDecoded?.type === 'waiting' && !msg.contentDecoded?.content.seatName)
    return !seat && !waitingNearBy
  }

  function createCustom() {
    const zoneCode = useZone().value.zoneCode!
    return JSON.stringify({
      ...(currentService.value?.serviceRecordId ? currentService.value : {}),
      visitorCode: isVisitor.value ? useVisitorCode(zoneCode).value : '',
      zoneCode: useZone().value.zoneCode,
    })
  }

  return {
    imToken,
    imAccount,
    isVisitor,
    connected,
    connectDone,
    reconnecting,
    messages,
    addMessage,
    deleteMessage,
    addSystemMessage,
    currentService,
    canChat,
    historyDone,
    historyLoading,
    historyExhaust,
    loadMoreHistory,
    updateDb,
    settings,
    clearSettings,
    initSettings,
    waiting,
    createCustom,
    processCurrentService,
    showUrgentNotice,
  }
})
